import { z } from "zod";
import {
  allowedTransitions,
  evaluatePublicationReadiness,
  publicationRequirementKeys,
  statusLabels,
  validateTransition,
  vehicleStatuses,
  writeOffReasonLabels,
  writeOffReasons,
  type TransitionSnapshot,
  type TransitionValidation,
  type PublicationReadiness,
  type PublicationReadinessInput,
  type PublicationRequirement,
  type PublicationRequirementKey,
  type VehicleStatus,
  type WriteOffReason,
} from "../domain/fiscal/status";
import { acquisitionModes, supplierKinds } from "../domain/fiscal/tax-flow";
import {
  DOCUMENT_NORMALIZER_MAX_INPUT_BYTES,
  formatForUpload,
  normalizableContentTypeSchema,
} from "./document-normalization";

// La máquina de once estados vive en src/domain/fiscal/status.ts. Este módulo
// solo reexporta ese vocabulario: mantener una segunda copia aquí es
// exactamente el defecto que la Task 11 elimina.
export {
  allowedTransitions,
  evaluatePublicationReadiness,
  publicationRequirementKeys,
  statusLabels,
  validateTransition,
  vehicleStatuses,
  writeOffReasonLabels,
  writeOffReasons,
};
export type {
  PublicationReadiness,
  PublicationReadinessInput,
  PublicationRequirement,
  PublicationRequirementKey,
  TransitionSnapshot,
  TransitionValidation,
  VehicleStatus,
  WriteOffReason,
};

// Task 11 (Fix 2): único criterio de "vehículo vendible", compartido por servidor (sales.ts,
// que es quien lo hace cumplir) y cliente (CommercialPage.tsx, que filtra el desplegable como
// comodidad). El sistema exige decisión explícita al añadir nuevos estados.
export const saleStartableStatuses: readonly VehicleStatus[] = ["in_preparation", "available", "reserved"];

export const documentTypes = [
  "technical_sheet",
  "registration_certificate",
  "service_book",
  "traffic_report",
  "itv",
  "coc",
  "purchase_contract",
  "sale_contract",
  "invoice",
  "warranty",
  "transfer_mandate",
  "other",
] as const;

export type DocumentType = (typeof documentTypes)[number];

export const documentTypeLabels: Record<DocumentType, string> = {
  technical_sheet: "Ficha técnica",
  registration_certificate: "Permiso de circulación",
  service_book: "Libro de revisiones",
  traffic_report: "Informe de tráfico",
  itv: "ITV",
  coc: "Certificado de conformidad (COC)",
  purchase_contract: "Contrato de compra",
  sale_contract: "Contrato de venta",
  invoice: "Factura",
  warranty: "Garantía",
  transfer_mandate: "Mandato de transferencia",
  other: "Otro documento",
};

export const roles = ["admin", "sales", "mechanic", "office", "external_manager"] as const;
export type UserRole = (typeof roles)[number];

export const roleLabels: Record<UserRole, string> = {
  admin: "Administrador",
  sales: "Comercial",
  mechanic: "Mecánico / taller",
  office: "Administrativo",
  external_manager: "Gestor externo",
};

export const vehicleStatusSchema = z.enum(vehicleStatuses);
export const writeOffReasonSchema = z.enum(writeOffReasons);
export const documentTypeSchema = z.enum(documentTypes);

// Task 13e: el depositante de un vehículo en depósito (venta en gestión) se modela como
// `suppliers` -para que su PII quede cifrada en el mismo sitio que cualquier otro proveedor, en
// vez de un campo de texto plano en `vehicles`-, así que POST /vehicles necesita una forma de
// recibirlo. `kind` por defecto 'private': el caso normal es que un particular deje su coche en
// gestión. Pero no se fija a 'private': un concesionario también puede tener en depósito el
// stock de otro concesionario, así que se acepta cualquier SupplierKind -mismo vocabulario que
// supplierKindSchema (src/shared/acquisition.ts), reexpresado aquí para no crear una dependencia
// de shared/domain.ts hacia shared/acquisition.ts que hoy no existe-.
export const consignmentDepositorSchema = z.object({
  kind: z.enum(supplierKinds).default("private"),
  name: z.string().trim().min(1).max(160),
  taxId: z.string().trim().max(32).optional(),
  email: z.string().trim().toLowerCase().email().max(200).optional(),
  phone: z.string().trim().max(40).optional(),
  address: z.string().trim().max(240).optional(),
});

export const quickVehicleSchema = z.object({
  registration: z.string().trim().max(16).optional(),
  vin: z.string().trim().max(32).optional(),
  make: z.string().trim().max(80).optional(),
  model: z.string().trim().max(120).optional(),
  version: z.string().trim().max(160).optional(),
  mileage: z.number().int().min(0).max(5_000_000).optional(),
  targetPriceCents: z.number().int().min(0).max(1_000_000_000).optional(),
  origin: z.enum(["national", "imported"]).default("national"),
  status: vehicleStatusSchema.default("draft"),
  acquisitionMode: z.enum(acquisitionModes).default("owned"),
  depositor: consignmentDepositorSchema.optional(),
}).refine((value) => value.acquisitionMode !== "consignment" || Boolean(value.depositor), {
  // Task 13e: se exige a propósito, aun a costa de romper altas de depósito que antes
  // funcionaban sin depositante (ver el informe de la tarea para el recuento). Un vehículo en
  // depósito sin depositante no puede aparecer nunca con transmitente en el libro policial -es
  // justo el hueco que esta tarea cierra-, así que dejarlo opcional habría dejado la puerta
  // abierta a repetir el mismo vacío legal que motivó el fix.
  message: "Un vehículo en depósito necesita los datos del depositante para el libro policial.",
  path: ["depositor"],
});

export const vehiclePatchSchema = z.object({
  registration: z.string().trim().max(16).nullable().optional(),
  vin: z.string().trim().max(32).nullable().optional(),
  make: z.string().trim().max(80).nullable().optional(),
  model: z.string().trim().max(120).nullable().optional(),
  version: z.string().trim().max(160).nullable().optional(),
  firstRegistrationAt: z.string().date().nullable().optional(),
  fuel: z.string().trim().max(40).nullable().optional(),
  transmission: z.string().trim().max(40).nullable().optional(),
  powerHp: z.number().int().min(0).max(2500).nullable().optional(),
  environmentalBadge: z.string().trim().max(24).nullable().optional(),
  color: z.string().trim().max(60).nullable().optional(),
  mileage: z.number().int().min(0).max(5_000_000).nullable().optional(),
  purchaseCostCents: z.number().int().min(0).max(1_000_000_000).nullable().optional(),
  targetPriceCents: z.number().int().min(0).max(1_000_000_000).nullable().optional(),
  finalSalePriceCents: z.number().int().min(0).max(1_000_000_000).nullable().optional(),
  buyerName: z.string().trim().max(160).nullable().optional(),
  taxRegime: z.enum(["rebu", "general"]).optional(),
  locationName: z.string().trim().max(120).nullable().optional(),
  provisionalRegistrationExpiresAt: z.string().date().nullable().optional(),
  reservedDepositCents: z.number().int().min(0).max(1_000_000_000).nullable().optional(),
  depositReceived: z.boolean().optional(),
});

export const transitionSchema = z.object({
  toStatus: vehicleStatusSchema,
  writeOffReason: writeOffReasonSchema.optional(),
  note: z.string().trim().max(500).optional(),
});

export const documentUploadSchema = z.object({
  type: documentTypeSchema,
  fileName: z.string().trim().min(1).max(240),
  contentType: z.enum([
    "application/pdf",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/jpeg",
    "image/png",
    "image/webp",
  ]),
  sizeBytes: z.number().int().positive().max(25 * 1024 * 1024),
  expiresAt: z.string().date().nullable().optional(),
}).superRefine((value, context) => {
  const normalizableType = normalizableContentTypeSchema.safeParse(value.contentType);
  if (!normalizableType.success) return;
  if (value.sizeBytes > DOCUMENT_NORMALIZER_MAX_INPUT_BYTES) {
    context.addIssue({
      code: "custom",
      path: ["sizeBytes"],
      message: "Los PDF y Excel no pueden superar 10 MB.",
    });
  }
  if (!formatForUpload(value.fileName, normalizableType.data)) {
    context.addIssue({
      code: "custom",
      path: ["fileName"],
      message: "La extensión del archivo no coincide con su formato.",
    });
  }
});

export const photoCategories = ["exterior", "interior", "engine", "defect", "document"] as const;
export const photoCategorySchema = z.enum(photoCategories);
export type PhotoCategory = z.infer<typeof photoCategorySchema>;

export const photoUploadSchema = z.object({
  category: photoCategorySchema,
  fileName: z.string().trim().min(1).max(240),
  contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  sizeBytes: z.number().int().positive().max(25 * 1024 * 1024),
  sha256: z.string().regex(/^[0-9a-f]{64}$/).optional(),
});

export const photoOrderSchema = z.object({
  photoIds: z.array(z.string().min(1).max(100)).min(1).max(500)
    .refine((ids) => new Set(ids).size === ids.length, "No repitas la misma foto."),
});

export const photoCategoryUpdateSchema = z.object({
  photoIds: z.array(z.string().uuid()).min(1).max(100)
    .refine((items) => new Set(items).size === items.length, "No repitas la misma foto."),
  category: photoCategorySchema,
});

export type PhotoCategoryUpdateInput = z.infer<typeof photoCategoryUpdateSchema>;

export type QuickVehicleInput = z.infer<typeof quickVehicleSchema>;
export type VehiclePatchInput = z.infer<typeof vehiclePatchSchema>;
export type TransitionInput = z.infer<typeof transitionSchema>;
export type DocumentUploadInput = z.infer<typeof documentUploadSchema>;

// Task 10: el helper económico y su input legados se retiran de aquí. Ese helper era el bug
// que la tarea existe para arreglar (restaba los gastos de la base imponible REBU y trataba el
// IVA deducible como coste); la sustituye el motor real en src/domain/fiscal/{cost,margin}.ts,
// resuelto en src/worker/repository.ts (vehicleEconomics) y expuesto además como endpoint
// dedicado en GET /vehicles/:vehicleId/economics. `EconomicsResult` se mantiene: sigue siendo
// el contrato que consumen VehicleListItem/VehicleDetail/DashboardData y el cliente.
export interface EconomicsResult {
  // null cuando el coste de adquisición no se conoce todavía (sin fila en
  // `purchases`): nunca se sustituye por 0, porque eso fabricaría un margen.
  purchaseCostCents: number | null;
  expensesCents: number;
  totalCostCents: number;
  referencePriceCents: number | null;
  grossMarginCents: number | null;
  estimatedVatCents: number | null;
  estimatedNetMarginCents: number | null;
  isActual: boolean;
}

export interface ProgressInput extends PublicationReadinessInput {
  purchaseCostCents?: number | null;
  buyerName?: string | null;
  documentTypes: readonly string[];
}

export interface ProgressLayer {
  key: "identification" | "documentation" | "economics" | "commercial";
  label: string;
  score: number;
}

export interface UnlockProgress {
  score: number;
  layers: ProgressLayer[];
  publish: PublicationReadiness;
  nextAction: { title: string; benefit: string; action: string } | null;
}

export function evaluateProgress(input: ProgressInput): UnlockProgress {
  const publication = evaluatePublicationReadiness(input);
  const hasIdentity = Boolean(input.registration || input.vin);
  const hasCoreSpecs = Boolean(input.make && input.model && input.mileage !== null && input.mileage !== undefined);
  const hasTechnicalDocument = input.documentTypes.includes("technical_sheet");
  const hasCirculation = input.documentTypes.includes("registration_certificate");
  const hasPurchaseCost = input.purchaseCostCents !== null && input.purchaseCostCents !== undefined;
  const hasTargetPrice = input.targetPriceCents !== null && input.targetPriceCents !== undefined;
  const hasMinimumPhotos = input.photoCount >= input.minPublishPhotos;

  const layers: ProgressLayer[] = [
    {
      key: "identification",
      label: "Identificación",
      score: Math.round(((Number(hasIdentity) + Number(hasCoreSpecs)) / 2) * 100),
    },
    {
      key: "documentation",
      label: "Documentación",
      score: Math.round(((Number(hasTechnicalDocument) + Number(hasCirculation)) / 2) * 100),
    },
    {
      key: "economics",
      label: "Económico",
      score: Math.round(((Number(hasPurchaseCost) + Number(hasTargetPrice)) / 2) * 100),
    },
    {
      key: "commercial",
      label: "Comercial",
      score: Math.round(((Number(hasTargetPrice) + Number(hasMinimumPhotos) + Number(Boolean(input.buyerName))) / 3) * 100),
    },
  ];

  let nextAction: UnlockProgress["nextAction"] = null;
  if (input.taxFlow === null || input.taxFlow === undefined) {
    nextAction = {
      title: "Registra la entrada del vehículo",
      benefit: "Determinamos la fiscalidad y abrimos el camino para ponerlo a la venta.",
      action: "register_purchase",
    };
  } else if (!hasTechnicalDocument) {
    nextAction = {
      title: "Sube la ficha técnica",
      benefit: "Te rellenamos las especificaciones y dejamos el anuncio casi listo.",
      action: "upload_technical_sheet",
    };
  } else if (!hasPurchaseCost) {
    nextAction = {
      title: "Añade el coste de compra",
      benefit: "Verás el margen real en cada decisión.",
      action: "add_purchase_cost",
    };
  } else if (!hasMinimumPhotos) {
    nextAction = {
      title: `Añade ${input.minPublishPhotos - input.photoCount} fotos`,
      benefit: "Desbloqueas la publicación en todos los canales.",
      action: "add_photos",
    };
  } else if (!hasTargetPrice) {
    nextAction = {
      title: "Define el precio objetivo",
      benefit: "Activamos el margen previsto y la publicación.",
      action: "add_target_price",
    };
  }

  return {
    score: Math.round(layers.reduce((sum, layer) => sum + layer.score, 0) / layers.length),
    layers,
    publish: publication,
    nextAction,
  };
}

export function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  const absoluteCents = Math.abs(cents);
  const euros = Math.floor(absoluteCents / 100);
  const decimalCents = absoluteCents % 100;
  const localeInteger = new Intl.NumberFormat("es-ES", {
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(euros);
  // Intl deja los importes de cuatro cifras sin agrupar en es-ES. En una UI
  // financiera resulta más legible mantener el millar de forma sistemática.
  const groupedInteger = euros >= 1_000 && euros < 10_000 && !localeInteger.includes(".")
    ? `${String(euros).slice(0, 1)}.${String(euros).slice(1)}`
    : localeInteger;
  const signedInteger = cents < 0 ? `-${groupedInteger}` : groupedInteger;
  return decimalCents === 0
    ? `${signedInteger} €`
    : `${signedInteger},${String(decimalCents).padStart(2, "0")} €`;
}

export function daysBetween(from: string, to = new Date()): number {
  const start = new Date(from).getTime();
  const end = to.getTime();
  return Math.max(0, Math.floor((end - start) / 86_400_000));
}
